/**
 * A clock that advances once a second while a run is live, and stops dead when it is not.
 *
 * Exists because a run's elapsed time is the one number on this surface that changes without the
 * server saying anything — and calling `Date.now()` during render is impure (the React Compiler
 * rejects it), so the tick has to be state a component subscribes to.
 *
 * `active` is the whole API: pass `isRunning(run)`. A finished run's duration is derived from its
 * own two timestamps and needs no clock at all, so an inactive hook schedules no timer.
 */
/* eslint-disable react-hooks/set-state-in-effect -- wall-clock time is external state that nothing
   in React notifies us about. Every path into it is a timer writing state from an effect; the only
   alternative, calling Date.now() at render, is impure and rejected by react-hooks/purity. The rule
   is disabled for this file alone, which exists solely to own that bridge. */
import { useEffect, useState } from 'react';

const TICK_MS = 1000;

export function useTickingNow(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!active) return;
    // Re-sync immediately: a run that just went live would otherwise show a timestamp up to a
    // second stale, and on a sub-second run that is the whole duration.
    setNow(Date.now());
    const timer = setInterval(() => {
      setNow(Date.now());
    }, TICK_MS);
    return () => {
      clearInterval(timer);
    };
  }, [active]);

  return now;
}
